import { getRequest, postRequest } from "../http/httpRequest";
import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import { turboField } from "../helpers/TurboStore";
import { VOICE_LOCAL_CODES } from "../../interfaces/VoiceObject";
import {
  VoiceApiError,
  isAbortError,
  readVoiceResponse,
  voiceAudioFileName,
} from "../helpers/VoiceApiResponseHelper";
import type { VoiceClip } from "../../interfaces/VoiceObject";
import type { ProductDetailsModal } from "../../interfaces/ProductObject";
import type {
  VoiceProductCreateContext,
  VoiceProductCreateResult,
  VoiceCreationAnswers,
} from "../../interfaces/VoiceProductCreateObject";

function isCreationResult(value: unknown): value is VoiceProductCreateResult {
  return (
    !!value &&
    typeof value === "object" &&
    "requestId" in value &&
    typeof value.requestId === "string" &&
    "status" in value
  );
}
export async function getVoiceCreationDraft(
  id: string,
): Promise<VoiceProductCreateResult> {
  return readVoiceResponse(
    await getRequest(
      `${ServiceEndpoints.VOICE_CREATE_REQUESTS}/${encodeURIComponent(id)}`,
      true,
    ),
    isCreationResult,
  );
}
export async function getSavedVoiceDrafts(): Promise<
  VoiceProductCreateResult[]
> {
  return readVoiceResponse(
    await getRequest(ServiceEndpoints.VOICE_CREATE_REQUESTS, true),
    (value): value is VoiceProductCreateResult[] =>
      Array.isArray(value) && value.every(isCreationResult),
    true,
  );
}
export async function discardVoiceCreation(id: string): Promise<void> {
  await readVoiceResponse(
    await postRequest(
      ServiceEndpoints.VOICE_OUTCOME.replace("{requestId}", id),
      { outcome: "DISCARDED" },
      true,
    ),
    (value): value is undefined => value === undefined,
  );
}
export async function answerVoiceCreation(
  id: string,
  input: VoiceCreationAnswers,
  clip: VoiceClip | undefined,
  signal: AbortSignal,
): Promise<VoiceProductCreateResult> {
  const form = new FormData();
  form.append(
    "context",
    new Blob(
      [
        JSON.stringify({
          ...input,
          ...(clip
            ? {
                durationMs: clip.durationMs,
                mimeType: clip.mimeType,
                locale: navigator.language,
              }
            : {}),
        }),
      ],
      { type: "application/json" },
    ),
  );
  if (clip) form.append("audio", clip.blob, voiceAudioFileName(clip.mimeType));
  return readVoiceResponse(
    await postRequest(
      `${ServiceEndpoints.VOICE_CREATE_REQUESTS}/${encodeURIComponent(id)}/answers`,
      form,
      true,
      undefined,
      { signal },
    ),
    isCreationResult,
  );
}

export async function analyzeProductCreation(
  files: File[],
  clip: VoiceClip | null,
  context: VoiceProductCreateContext,
  clientRequestId: string,
  signal: AbortSignal,
  text?: string,
): Promise<VoiceProductCreateResult> {
  const form = new FormData();
  // A typed/pasted description replaces the recording.
  if (clip) form.append("audio", clip.blob, voiceAudioFileName(clip.mimeType));
  files.forEach((file) => form.append("images", file, file.name));
  form.append(
    "context",
    new Blob(
      [
        JSON.stringify({
          clientRequestId,
          durationMs: clip?.durationMs,
          mimeType: clip?.mimeType,
          locale: navigator.language,
          context,
          ...(clip ? {} : { text }),
          ...turboField(context.businessId),
        }),
      ],
      { type: "application/json" },
    ),
  );
  const endpoint = ServiceEndpoints.VOICE_ANALYZE.replace(
    "{skillId}",
    "product.create",
  );
  let response: Response;
  try {
    response = await postRequest(endpoint, form, true, undefined, { signal });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new VoiceApiError(VOICE_LOCAL_CODES.NETWORK, "Network error", {
      retryable: true,
    });
  }
  return readVoiceResponse(
    response,
    (value): value is VoiceProductCreateResult =>
      value !== null &&
      typeof value === "object" &&
      "requestId" in value &&
      "status" in value &&
      typeof value.requestId === "string" &&
      ["READY", "NEEDS_INPUT", "NOT_UNDERSTOOD"].includes(String(value.status)),
  );
}

/** Voice-only publication. Snapshot/recovery state belongs to VoiceProductCreateDb and its thunk. */
export async function publishVoiceProduct(
  payload: unknown,
  creationId: string,
  requestId: string,
  signal: AbortSignal,
): Promise<ProductDetailsModal> {
  const response = await postRequest(
    ServiceEndpoints.VOICE_CREATE_PUBLISH,
    payload,
    true,
    {
      "Idempotency-Key": creationId,
      "X-Voice-Request-Id": requestId,
    },
    { signal },
  );
  const result = await readVoiceResponse(
    response,
    (value): value is { products: ProductDetailsModal[] } => {
      if (
        !value ||
        typeof value !== "object" ||
        !("products" in value) ||
        !Array.isArray(value.products)
      )
        return false;
      const product: unknown = value.products[0];
      return (
        !!product &&
        typeof product === "object" &&
        "productId" in product &&
        typeof product.productId === "string"
      );
    },
  );
  return result.products[0];
}
